import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { browserHistory } from 'react-router';
import ReactDOM from 'react-dom';

import { SetZillowPlaceType, SetZillowPlaceTypeOptions, SetZillowRanking } from '../../actions';
import withStyles from '../../decorators/withStyles';
import { abbrevLower, asyncForEach, commas, formatZillowValue, getDefaultZillowPlaceType,
  getGeoContexts, getPlaceTypeOptions, isUS, pplural, togglePanel, xlt } from "../../utils/utils";
import RiskFinderComponent from '../RiskFinderComponent';
import styles from './ZillowMap.css';


const topojson = require('topojson');
const d3 = require('d3');
d3.tip = require('d3-tip').default;


class ZillowMap extends RiskFinderComponent {

  static propTypes = {
    zillowData: PropTypes.object.isRequired,
    zillowEndYr: PropTypes.string.isRequired,
    zillowIsPct: PropTypes.bool.isRequired,
    zillowModel: PropTypes.string.isRequired,
    zillowPathway: PropTypes.string.isRequired,
    zillowPlaceType: PropTypes.string.isRequired,
    zillowVarType: PropTypes.string.isRequired,
    zillowYear: PropTypes.string.isRequired,
    zillowYrBuilt: PropTypes.string.isRequired,
    noData: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      data: {},
    };
    this.nullColor = '#eee';
  }

  static defaultProps = {
    width: 400,
    height: 400,
    margin: {
      left: 10,
      right: 10,
      top: 10,
      bottom: 10,
    },
  };

  componentWillUnmount() {
    var el = ReactDOM.findDOMNode(this).firstChild.firstChild;
    this.destroy(el);
  }

  componentDidUpdate(props) {
    this.updateMap(props);
  }

  getGeoPlaceTypeData = () => {
    const { zillowPlaceType } = this.props;
    const { geo } = this.state.data;
    return geo[zillowPlaceType];
  };

  hasGeoData() {
    return Boolean(this.getGeoPlaceTypeData());
  }

  getApiEndpoints(contexts) {
    const { placeID, placeType } = this.props;
    const urls = [];
    for (let context of contexts) {
      urls.push(`/api/geometry/${placeType}/${placeID}/${context}`);
    }
    return urls;
  }

  async getGeoData() {
    const { placeID, placeType } = this.props;
    const geoContexts = getGeoContexts(placeID, placeType);
    const urls = this.getApiEndpoints(geoContexts);
    const geoData = [];
    await asyncForEach(urls, async (url) => {
      await d3.json(url)
        .then((data) => {
          geoData.push(data);
        })
        .catch((err) => {
          geoData.push({});
        });
    });
    const formattedData = {};
    for (let i = 0; i < geoContexts.length; i++) {
      const thisPlaceType = geoContexts[i] === 'peer' ? placeType : geoContexts[i];
      if (Object.keys(geoData[i]).length > 0) {
        formattedData[thisPlaceType] = geoData[i];
      }
    }
    return formattedData;
  }

  async updateZillowPlaceType() {
    const { placeType } = this.props;
    const defaultPlaceType = getDefaultZillowPlaceType(placeType);
    this.props.dispatch(SetZillowPlaceType(defaultPlaceType));
  }

  async updateZillowPlaceTypeOptions(geoData) {
    const { placeID, placeType } = this.props;
    const placeTypeOptions = await getPlaceTypeOptions(geoData, placeID, placeType);
    this.props.dispatch(SetZillowPlaceTypeOptions(placeTypeOptions));  
  }

  async loadData() {
    const self = this;
    const geoData = await this.getGeoData();
    await this.updateZillowPlaceTypeOptions(geoData);
    if (this.hasData() && this.hasGeoData()) {
      const cb = () => { self.updateGeo(ReactDOM.findDOMNode(self)); };
      this.setState({data: {geo: geoData}}, cb);
    } else {
      this.setState({data: {geo: geoData}}, this.create);
    }
  }

  updateZillowRanking() {
    const zillowRanking = this.getZillowRanking();
    this.props.dispatch(SetZillowRanking(zillowRanking));
  }

  async updateMap(props) {
    if (props.place !== this.props.place
        || props.placeID !== this.props.placeID
        || props.placeType !== this.props.placeType
        || props.zillowData !== this.props.zillowData
        || !this.hasGeoData()) {
      await this.updateZillowPlaceType();
      await this.loadData();
    } else if (props.zillowPlaceType !== this.props.zillowPlaceType) {
      try {
        this.updateGeo(ReactDOM.findDOMNode(this));
      } catch (err) {
        await this.loadData();
      }   
    } else if (props.zillowIsPct !== this.props.zillowIsPct
        || props.zillowModel !== this.props.zillowModel
        || props.zillowPathway !== this.props.zillowPathway
        || props.zillowVarType !== this.props.zillowVarType
        || props.zillowYear !== this.props.zillowYear
        || props.zillowYrBuilt !== this.props.zillowYrBuilt) {
      this.update();
    }
  }

  validNavTarget(d) {
    const { noData } = this.props;
    const placeTypeData = this.getZillowPlaceTypeData();
    return placeTypeData[d.properties.placeID] && !noData(d);
  }

  navigateTo = (d) => {
    window.scrollTo(0, 0);
    browserHistory.push({pathname: `/${d.properties.placeType}/${d.properties.placeID}`});
  };

  getZillowVariable = () => {
    const { zillowVarType, zillowYrBuilt } = this.props;
    if (zillowVarType === 'Growth') {
      return 'riskyGrowthFactor';
    } else {
      return `Zillow${zillowVarType}${zillowYrBuilt}`;
    }
  }

  getGeoKey() {
    const { zillowPlaceType } = this.props;
    const { geo } = this.state.data;
    const geoObjKeys = Object.keys(geo[zillowPlaceType].objects);
    if (geoObjKeys.includes('boundary')) {
      return geoObjKeys.filter((k) => k !== 'boundary')[0];
    } else if (geoObjKeys.includes('City')) {
      return 'City';
    } else if (zillowPlaceType === 'congressional-district') {
      return 'CD';
    } else {
      return zillowPlaceType.charAt(0).toUpperCase() + zillowPlaceType.substr(1);
    }
  }

  getValue(obj) {
    const { zillowIsPct } = this.props;
    const zillowVariable = this.getZillowVariable();
    if (zillowVariable === 'riskyGrowthFactor') {
      return obj.riskyGrowthFactor;
    } else {
      return zillowIsPct ? obj[zillowVariable].percent_exposed : obj[zillowVariable].exposed;
    }
  }

  getZillowRanking() {
    const { zillowData, zillowIsPct, zillowModel, zillowPathway, zillowPlaceType, zillowYear } = this.props;
    const { geo } = this.state.data;

    const zillowVariable = this.getZillowVariable();
    const geoKey = this.getGeoKey();

    const impactVals = this.getZillowPlaceTypeData();
    const geometries = geo[zillowPlaceType].objects[geoKey].geometries;

    const placeIDs = Object.keys(impactVals).filter((k) => zillowVariable in impactVals[k]);

    placeIDs.sort((a, b) => {
      return this.getValue(impactVals[a]) > this.getValue(impactVals[b]) ? -1 : 1;
    });

    const zillowRankings = [];
    let placeIndex = 0;
    let rankingIndex = 0;
    
    while (placeIndex < placeIDs.length && rankingIndex < 5) {
      const thisPlaceID = placeIDs[placeIndex];
      const geometry = geometries.filter((geom) => geom.properties.placeID === thisPlaceID)[0];
      const zillowVal = this.getValue(impactVals[thisPlaceID]);
      
      if (geometry && zillowVal > 0) {
        zillowRankings.push({
          path: `${geometry.properties.placeType}/${geometry.properties.placeID}`,
          name: geometry.properties.name,
          value: zillowVal,
        });
        rankingIndex++;
      }
      placeIndex++;
    }

    return zillowRankings;
  }

  isThisPlace(d) {
    return d.properties.placeID === this.props.placeID && d.properties.placeType === this.props.placeType;
  }

  getTooltipText = (place, placeData) => {
    const { zillowEndYr, zillowIsPct, zillowVarType, zillowYear, zillowYrBuilt } = this.props;
    const zillowVariable = this.getZillowVariable();
    const hasValue = placeData.hasOwnProperty(zillowVariable);
    let formattedValue, formattedHomeValValue, formattedHomeCntValue, formattedGrowthValue;
    if (!hasValue) {
      formattedValue = 'N/A (no areas at risk)';
    } else if (zillowYrBuilt === 'Since2010' && placeData.ZillowCountSince2010.total === 0) {
      formattedValue = 'No homes built after 2009.';
    } else {
      const valueVarName =`ZillowZestimate${zillowYrBuilt}`;
      const valueVar = placeData[valueVarName].exposed;
      const valueVarPct = placeData[valueVarName].percent_exposed;
      const formattedValueVar = formatZillowValue(valueVar, 'Zestimate', false);
      const formattedValueVarPct = formatZillowValue(valueVarPct, 'Zestimate', true);

      const countVarName = `ZillowCount${zillowYrBuilt}`;
      const countVar = placeData[countVarName].exposed;
      const countVarPct = placeData[countVarName].percent_exposed;
      const formattedCountVar = formatZillowValue(countVar, 'Count', false);
      const formattedCountVarPct = formatZillowValue(countVarPct, 'Count', true);

      const growthVar = placeData.riskyGrowthFactor;
      formattedGrowthValue = formatZillowValue(growthVar, 'Growth', false, zillowEndYr);

      if (formattedGrowthValue.endsWith('x')) {
        const rawVal = parseFloat(formattedGrowthValue.replace('x', ''));
        const fast = rawVal > 1 ? 'faster' : 'as fast';
        const than = rawVal > 1 ? 'than' : 'as';
        formattedGrowthValue = `Recent growth (<var>2010-${zillowEndYr}</var>) has been ${formattedGrowthValue} ${fast}<br/>in the risk zone ${than} in safer areas`;
      }
      const noun = countVar === 1 ? 'home' : 'homes';
      const adj = zillowYrBuilt === 'Since2010' ? 'built after 2009 ' : '';
      const verb = countVar === 1 ? 'is' : 'are';
      const bold = ' style="font-weight: bold;"';
      const hide = ' style="display: none;"';

      const growthStyle = zillowEndYr === '2005' ? hide : zillowVarType === 'Growth' ? bold : '';
      const countStyle = zillowVarType === 'Count' && !zillowIsPct ? bold : '';
      const countPctStyle = zillowVarType === 'Count' && zillowIsPct ? bold : '';
      const valueStyle = zillowVarType === 'Zestimate' && !zillowIsPct ? bold : '';
      const valuePctStyle = zillowVarType === 'Zestimate' && zillowIsPct ? bold : '';
      const qualifier = zillowYrBuilt === 'Since2010' ? 'in this category ' : '';

      formattedValue = '<div>' +
          `<div>For <var>${zillowYear}</var>, <var${countStyle}>${formattedCountVar}</var> ${noun} ${adj}${verb} at risk (<var${countPctStyle}>${formattedCountVarPct}</var>),<br/>` +
          `comprising <var${valueStyle}>${formattedValueVar}</var> of home value ${qualifier}(<var${valuePctStyle}>${formattedValueVarPct}</var>).</div>` +
          `<div${growthStyle}>${formattedGrowthValue}.</div>` +
        '</div>' +
        '<div class="nav-note">Select to navigate to this place page.</div>';        
    }
    return `<h4><var>${place}</var></h4><div>${formattedValue}</div>`;
  };

  create() {
    const { zillowIsPct, zillowVarType } = this.props;
    const zillowVariable = this.getZillowVariable();
    const element = ReactDOM.findDOMNode(this).firstChild.firstChild;
    var projection = d3.geoMercator()
      .scale(1)
      .translate([0, 0]);
    var path = d3.geoPath()
      .projection(projection);

    this.tip = d3.tip().attr("class", "d3-tip choropleth-tip zillow").direction("e").offset([0, 5])
      .html((d) => {
        const placeTypeData = this.getZillowPlaceTypeData();
        const placeData = placeTypeData[d.properties.placeID] || {};
        return this.getTooltipText(d.properties.name, placeData);
      });

    var svg = d3.select(element).append("svg")
      .attr("class", "choropleth-map")
      .attr("viewBox", "0 0 " + this.props.width + " " + this.props.height);

    svg.append("g")
      .attr("class", "choropleth-map-g")
      .append("path")
      .attr("d", path)
      .attr("class", "choropleth-mesh");

    svg.append("g")
      .attr("class", "choropleth-cities");

    svg.call(this.tip);

    d3.selectAll('.ZillowChoropleth .legend-svg .choropleth-legend').remove();

    var legend = d3.select(".ZillowChoropleth .legend-svg").append("svg")
      .attr("class", "choropleth-legend")
      .attr("height", "140px")
      .attr("viewBox", "0 0 140 70");

    legend.append("g")
      .attr("class", "boxes");

    this.updateGeo(ReactDOM.findDOMNode(this));
  }

  updateGeo(element) {
    var self = this;
    var geoData = this.getGeoPlaceTypeData();

    var projection = d3.geoMercator()
        .scale(1)
        .translate([0, 0]);
    var path = d3.geoPath()
        .projection(projection);

    var container;
    var points = {features: []};
    if ('City' in geoData.objects) {
      container = topojson.feature(geoData, geoData.objects.State);
      points = topojson.feature(geoData, geoData.objects.City);
    } else if ('boundary' in geoData.objects) {
      container = topojson.feature(geoData, geoData.objects.boundary);
      Object.keys(geoData.objects).forEach((k) => {
        if (k !== 'boundary') {
          if (geoData.objects[k].geometries[0].type === "Polygon" ||
              geoData.objects[k].geometries[0].type === "MultiPolygon") {
            container = topojson.feature(geoData, geoData.objects[k]);
          } else {
            points = topojson.feature(geoData, geoData.objects[k]);
          }
        }
      });
    } else {
      container = topojson.feature(geoData, geoData.objects[Object.keys(geoData.objects)[0]]);
    }

    // Scale and translate the projection to the bounding box of the container feature.
    // See https://bl.ocks.org/mbostock/4707858
    var width = this.props.width;
    var height = this.props.height;

    var b;
    if (this.props.choroplethBounds) {
      var bounds = this.props.choroplethBounds.split(",");
      bounds = [bounds[1], bounds[2], bounds[3], bounds[0]].map((v) => parseFloat(v));
      b = [projection(bounds.slice(0, 2)), projection(bounds.slice(2, 4))];
    } else {
      b = path.bounds(container);
    }
    var s = .95 / Math.max((b[1][0] - b[0][0]) / width, (b[1][1] - b[0][1]) / height),
      t = [(width - s * (b[1][0] + b[0][0])) / 2, (height - s * (b[1][1] + b[0][1])) / 2];
    const aspect = Math.abs((b[0][0]) - b[0][1]) / Math.abs((b[1][0] - b[1][1]));
    d3.select(element).selectAll(".choropleth-map").attr("viewBox", (function() {
      if (aspect > 1) {
        const val = 400 / aspect;
        return "0 " + ((400 - val) / 2).toString() + " 400 " + val.toString();
      } else {
        const val = 400 / aspect;
        return ((400 - val) / 2).toString() + " 0 " + val.toString() + " 400";
      }
    }()));
    projection.scale(s).translate(t);

    var paths = d3.select(element).selectAll(".choropleth-map-g").selectAll(".choropleth-mesh")
      .data(container.features);

    paths
      .attr("d", path)
      .attr("class", (d) => "choropleth-mesh" + (points.features.length === 0 ? " active" : ""))
      .on("mouseover", function(d) {
        if (points.features.length === 0) {
          d3.select(this)
            .style("stroke", "black")
            .style("stroke-width", 1);
          self.tip.show(d, this);
        }
      })
      .on("mouseout", function(d) {
        if (points.features.length === 0) {
          d3.select(this)
            .style("stroke", self.isThisPlace(d) ? "#027bbf" : "#4d4d4d")
            .style("stroke-width", self.isThisPlace(d) ? 2 : 0.5);
          self.tip.hide();
        }
      })
      .on("click", (d) => {
        if (points.features.length === 0 && self.validNavTarget(d)) {
          this.navigateTo(d);
        }
      });

    paths.enter().append("path")
      .attr("d", path)
      .attr("class", (d) => "choropleth-mesh" + (points.features.length === 0 ? " active" : ""))
      .on("mouseover", function(d) {
        if (points.features.length === 0) {
          d3.select(this)
            .style("stroke", "black")
            .style("stroke-width", 1);
          self.tip.show(d, this);
        }
      })
      .on("mouseout", function(d) {
        if (points.features.length === 0) {
          d3.select(this)
            .style("stroke", self.isThisPlace(d) ? "#027bbf" : "#4d4d4d")
            .style("stroke-width", self.isThisPlace(d) ? 2 : 0.5);
          self.tip.hide();
        }
      })
      .on("click", (d) => {
        if (points.features.length === 0 && self.validNavTarget(d)) {
          this.navigateTo(d);
        }
      });

    paths.exit().remove();

    var circles = d3.select(element).selectAll(".choropleth-cities").selectAll("circle")
      .data(points.features);

    circles
      .attr("cx", (d) => projection(d.geometry.coordinates)[0])
      .attr("cy", (d) => projection(d.geometry.coordinates)[1])
      .attr('r', '5')
      .on("mouseover", function(d) {
        d3.select(this)
          .style("stroke", "black")
          .style("stroke-width", 1);
        self.tip.show(d, this);
      })
      .on("mouseout", function(d) {
        d3.select(this)
          .style("stroke", self.isThisPlace(d) ? "#027bbf" : "#4d4d4d")
          .style("stroke-width", self.isThisPlace(d) ? 2 : 0.5);
        self.tip.hide();
      })
      .on("click", (d) => {this.validNavTarget(d) && this.navigateTo(d);});

    circles
      .enter().append("circle")
      .attr("cx", (d) => projection(d.geometry.coordinates)[0])
      .attr("cy", (d) => projection(d.geometry.coordinates)[1])
      .attr('r', '5')
      .on("mouseover", function(d) {
        d3.select(this)
          .style("stroke", "black")
          .style("stroke-width", 1);
        self.tip.show(d, this);
      })
      .on("mouseout", function(d) {
        d3.select(this)
          .style("stroke", self.isThisPlace(d) ? "#027bbf" : "#4d4d4d")
          .style("stroke-width", self.isThisPlace(d) ? 2 : 0.5);
        self.tip.hide();
      })
      .on("click", (d) => {this.validNavTarget(d) && this.navigateTo(d);});

    circles.exit().remove();

    this.update();
  }

  getDigits(num) {
    return parseInt(num.toExponential().match(/\+([0-9]+)/)[1]);
  }

  round(min, max) {
    const { zillowIsPct, zillowVarType } = this.props;
    if (zillowIsPct) {
      return (x) => x;
    } else {
      try {
        const maxDigits = this.getDigits(max);
        return (x) => {
          try {
            if (this.getDigits(x) < maxDigits) {
              return parseFloat(x.toPrecision(1));
            } else {
              return parseFloat(x.toPrecision(2));
            }
          } catch (err) {
            return x;
          }
        };
      } catch (err) {
        return (x) => x;
      }
    }
  }

  updateDefaultLegend(color, min, max, allValues) {
    const { zillowIsPct, zillowVarType } = this.props;
    const zillowVariable = this.getZillowVariable();
    const formatValue = (value) => formatZillowValue(value, zillowVarType, zillowIsPct);
    const round = this.round(min, max);
    const roundValues = allValues.map(round);
    roundValues.sort((a, b) => a < b ? 1 : -1);
    
    let values = [];

    var seenVals = new Set();
    roundValues.forEach((val) => {
      if (val !== undefined) {
        const col = color(val);
        if (!seenVals.has(col)) {
          values.push(val);
        }
        seenVals.add(col);        
      }
    });

    const textOffset = () => {
      if (zillowIsPct) {
        return 50;
      } else if (zillowVarType === 'Zestimate') {
        return 85;
      } else {
        return 55;
      }
    };

    const textTransform = (d) => {
      if (d === null) {
        return formatValue(0);
      }
      return formatValue(d);
    };

    var legend = d3.selectAll(".ZillowChoropleth .boxes")
      .selectAll("g").attr("class", "legend-box-g").data(values);

    legend.select("rect").attr("fill", (d) => color(round(d)));
    legend.select("text")
      .attr("x", textOffset())
      .text((d) => textTransform(d));

    var legendG = legend.enter().append("g");

    legendG
      .append("rect")
      .attr("x", 10)
      .attr("y", (d, i) => (10 * i))
      .attr("width", 10)
      .attr("height", 10)
      .attr("fill", (d) => color(round(d)));

    legendG
      .append("text")
      .attr("class", "notranslate")
      .attr("x", textOffset())
      .attr("y", (d, i) => 8 + (10 * i))
      .attr("text-anchor", "end")
      .text((d) => textTransform(d));

    legend.exit().remove();

    d3.select(".ZillowChoropleth .legend-svg .choropleth-legend").append("text")
      .attr("class", "legend-caption")
      .attr("y", (d) => 13 + (10 * d3.selectAll(".ZillowChoropleth .legend-svg .boxes g").size()))
      .text("Legend values are bin upper limits");
  }

  updateGrowthLegend(color) {
    const { zillowVarType } = this.props;
    const formatValue = (value) => formatZillowValue(value, zillowVarType);

    const values = {
      null: 'N/A (no known growth)',
      0: 'All growth in safer zone',
      1: '0.1-1.0x as fast in risk zone',
      2: '1.1-2.0x faster in risk zone',
      3: '> 2.0x faster in risk zone',
      Infinity: 'All growth in risk zone',
    };

    const textOffset = 135;
    const legendValues = [Infinity, 3, 2, 1, 0, null];

    var legend = d3.selectAll(".ZillowChoropleth .boxes")
      .selectAll("g").attr("class", "legend-box-g").data(legendValues);

    legend.select("rect").attr("fill", (d) => d === null ? this.nullColor : color(d));
    legend.select("text")
      .attr("x", textOffset)
      .text((d) => values[d]);

    var legendG = legend.enter().append("g");

    legendG
      .append("rect")
      .attr("x", 10)
      .attr("y", (d, i) => (10 * i))
      .attr("width", 10)
      .attr("height", 10)
      .attr("fill", (d) => d === null ? this.nullColor : color(d));

    legendG
      .append("text")
      .attr("class", "notranslate")
      .attr("x", textOffset)
      .attr("y", (d, i) => 8 + (10 * i))
      .attr("text-anchor", "end")
      .text((d) => values[d]);

    legend.exit().remove();
  }

  updateLegend(color, min, max, values) {
    const { zillowVarType } = this.props;
    d3.selectAll('.ZillowChoropleth .legend-caption').remove();
    if (zillowVarType === 'Growth') {
      this.updateGrowthLegend(color);
    } else {
      this.updateDefaultLegend(color, min, max, values);
    }
  }

  geoFilteredZillowVals(zillowData, geoData) {
    const geometries = geoData.objects[Object.keys(geoData.objects).filter((k) => k !== "boundary")[0]].geometries;
    const geoPlaceIDs = geometries.map((geom) => geom.properties.placeID);
    const filteredKeys = Object.keys(zillowData).filter((key) => geoPlaceIDs.includes(key));
    const filteredZillowData = Object.keys(zillowData)
      .filter((key) => filteredKeys.includes(key))
      .reduce((obj, key) => {
        return {
          ...obj,
          [key]: zillowData[key]
        };
      }, {});
    return filteredZillowData;
  }

  getColorRamp = (min, max) => {
    const { zillowIsPct, zillowVarType } = this.props;
    const brownRamp = ['#f7eee2', '#e8d1af', '#d6b47e', '#c1994d', '#ab7e15'];
    const blueRamp = ['#ecf5fa', '#b6c5d3', '#8197ae', '#4c6c8a', '#0B4467'];
    const mixedRamp = ['#5cdcd6', '#37b7ae','#ab7930','#8b5532','#67342a']; // blue and brown
    const domainMin = (min === max) ? 0 : min;

    if (zillowVarType === 'Growth') {
      const domain = [0.00001, 1.00001, 2.00001, Infinity];
      return d3.scaleThreshold().domain(domain).range(mixedRamp);
    } else {
      return d3.scaleQuantize()
        .domain([domainMin, max + 0.00001])
        .range(zillowIsPct ? blueRamp : brownRamp);
    }
  };

  getPlaceValue = (placeData) => {
    const { zillowIsPct, zillowVarType } = this.props;
    const zillowVariable = this.getZillowVariable();
    const zilVar = placeData[zillowVariable];
    if (zillowVarType === 'Growth') {
      return zilVar === null ? null : Math.ceil(zilVar);
    } else {
      const varData = zilVar ? zilVar : {percent_exposed: 0, exposed: 0};
      return zillowIsPct ? varData.percent_exposed : varData.exposed;
    }
  };

  getFillColor = (color, d, values) => {
    const { zillowIsPct, noData, zillowVarType } = this.props;
    if (!noData(d)) {
      const placeData = values[d.properties.placeID];
      if (placeData) {
        const value = this.getPlaceValue(placeData);
        return value === null ? this.nullColor : color(value);
      }
    }
    return "#fff";
  };

  getMapValues = (geoFilteredData) => {
    const { zillowIsPct } = this.props;
    const self = this;
    return Object.values(geoFilteredData).map((d) => self.getPlaceValue(d));
  }

  getZillowPlaceTypeData = () => {
    const { zillowData, zillowModel, zillowPathway, zillowPlaceType, zillowYear } = this.props;
    return zillowData[zillowModel][zillowPathway][zillowYear][zillowPlaceType];
  };

  getGeoFilteredData = () => {
    const zilData = this.getZillowPlaceTypeData();
    const geoData = this.getGeoPlaceTypeData();
    return this.geoFilteredZillowVals(zilData, geoData);
  };

  updateChoro = (element) => {
    const self = this;

    // only use Zillow data for places on the map
    const geoFilteredData = this.getGeoFilteredData();

    const values = this.getMapValues(geoFilteredData);

    const min = d3.min(values);
    const max = d3.max(values);

    const round = this.round(min, max);
    const color = this.getColorRamp(min, max);

    this.updateLegend(color, min, max, values);

    var points = d3.select(element).selectAll(".choropleth-cities").selectAll("circle");
    var elements;
    if (points.nodes().length > 0) {
      elements = points;
      d3.select(element).selectAll(".choropleth-mesh")
        .style("fill", "#fff")
        .style("stroke-width", 0.5)
        .style("stroke", "#4d4d4d");
    } else {
      elements = d3.select(element).selectAll(".choropleth-mesh");
    }

    elements
      .style("stroke-width", (d) => self.isThisPlace(d) ? 2 : 0.5)
      .style("stroke", (d) => {
        return self.isThisPlace(d) ? "#027bbf" : "#4d4d4d";
      })
      .style("fill", (d) => this.getFillColor(color, d, geoFilteredData));
  };

  getLegendTitle = () => {
    const { zillowIsPct, zillowVarType, zillowYear } = this.props;
    let title;
    if (zillowVarType === 'Zestimate') {
      title = zillowIsPct ? '% of total home value exposed' : 'Total value of homes at risk';
    } else if (zillowVarType === 'Count') {
      title = zillowIsPct ? '% of homes exposed' : 'Number of homes at risk';
    } else if (zillowVarType === 'Growth') {
      title = `Housing growth rate in areas at danger of at least yearly coastal flooding by ${zillowYear}, compared to safer areas`;
    }
    title = xlt(title);
    return zillowVarType === 'Growth' ? title : <div><br/><br/><span>{title}</span></div>;
  };

  update = () => {   
    if (this.hasGeoData()) {
      const element = ReactDOM.findDOMNode(this).firstChild.firstChild;
      this.updateChoro(element);
      this.updateZillowRanking();      
    }
  };

  destroy(element) {
    // TODO
  }

  render() {
    const legendTitle = this.getLegendTitle();

    return (
      <div className="ZillowChoropleth Choropleth">
        <div className="Choropleth-map"><div className="Choropleth-map-container"></div></div>
        <div className="Choropleth-legend">
          <h4 key={Math.random().toString()}>{legendTitle}</h4>
          <div className="legend-svg"></div>
        </div>
      </div>
    );
  }

}

export default compose(
  withStyles(styles),
  connect((state) => {return {isEmbed: state.isEmbed, choroplethBounds: state.choroplethBounds};}),
)(ZillowMap);
